import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import FullscreenOutlinedIcon from '@mui/icons-material/FullscreenOutlined';
import FullscreenExitOutlinedIcon from '@mui/icons-material/FullscreenExitOutlined';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import Player from '@vimeo/player';
import '../../assets/styles/video.css';
import { Box, Button, CircularProgress } from '@mui/material';
import { profile } from '../../api/services/user';
import { handleApiError } from '../../utils/errorHandler';
import { pushNotification } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { Video } from '../../api/interfaces/Video';
import { getVideoById, markVideoAsRecentlyWatched } from '../../api/services/video';

const WatchVimeoVideoPlayerPage: React.FC = () => {
    const navigate = useNavigate();
    const { videoId } = useParams();
    const outerContainerRef = useRef<HTMLDivElement>(null);
    const playerContainerRef = useRef<HTMLDivElement>(null);
    const playerInstanceRef = useRef<Player | null>(null);

    const [isFullscreen, setIsFullscreen] = useState(false);
    const [video, setVideo] = useState<Video | null>(null);
    const [watermarkCurrentEmail, setWatermarkCurrentEmail] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const fetchProfile = useCallback(async () => {
        try {
            const response = await profile();
            const user = response.data.user;
            setWatermarkCurrentEmail(user.email ?? '');
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    const fetchVideo = useCallback(async () => {
        try {
            if (!videoId) throw new Error('Video ID is missing');
            const response = await getVideoById(videoId);
            setVideo(response.data.video);
        } catch (error) {
            console.error(error);
        }
    }, [videoId]);

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            await Promise.all([fetchProfile(), fetchVideo()]);
            setIsLoading(false);
            // Mark video as recently watched in background
            markVideoAsRecentlyWatched(videoId as string);
        };
        init();
    }, [fetchProfile, fetchVideo, videoId]);

    useEffect(() => {
        if (playerContainerRef.current && video?.videoUrl2 && watermarkCurrentEmail) {
            // For backup if vimeo player is needed in future
            // if (playerInstanceRef.current) {
            //     playerInstanceRef.current.destroy().catch((error) => {
            //         console.error('Failed to destroy existing player:', error);
            //     });
            // }

            // const player = new Player(playerContainerRef.current, {
            //     url: video.videoUrl2,
            //     autoplay: true,
            //     title: false,
            //     byline: false,
            //     portrait: false,
            // });

            // playerInstanceRef.current = player;

            // return () => {
            //     player.destroy().catch((error) => {
            //         console.error('Failed to destroy player on cleanup:', error);
            //     });
            // };
        }
    }, [video, watermarkCurrentEmail]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            outerContainerRef.current?.requestFullscreen().catch(console.error);
        } else {
            document.exitFullscreen().catch(console.error);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    return (
        <div
            ref={outerContainerRef}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                margin: 0,
                padding: 0,
                zIndex: 9999,
                backgroundColor: 'black',
            }}
        >
            {isLoading ? (
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 10,
                    }}
                >
                    <CircularProgress size={100} sx={{ color: "#fff" }} />
                </div>
            ) : (
                <>
                    <div className="watermark-container" style={{ position: 'relative', width: '100%', height: '100%' }}>
                        {/* <div
                            ref={playerContainerRef}
                            style={{
                                width: '100%',
                                height: '100%',
                                position: 'relative',
                            }}
                        /> */}
                        {/* Use iFrame instead of vimeo player to fix fullscreen on keyboard press issue */}
                        <iframe
                            src={`${video?.videoUrl2}?autoplay=1&title=0&byline=0&portrait=0&fullscreen=0`}
                            width="100%"
                            height="100%"
                            allow="autoplay; encrypted-media"
                            allowFullScreen={false}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh',
                                border: 'none',
                                margin: 0,
                                padding: 0,
                                backgroundColor: 'black',
                                display: 'block',
                            }}
                        ></iframe>
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 60,
                                right: 10,
                                color: 'white',
                                fontSize: '16px',
                                fontWeight: 600,
                                opacity: 0.7,
                                pointerEvents: 'none',
                                userSelect: 'none',
                            }}
                        >
                            {watermarkCurrentEmail}
                        </Box>
                        <div className="watermark-overlay">
                            {Array.from({ length: 20 }).map((_, rowIndex) => (
                                <div
                                    key={rowIndex}
                                    style={{
                                        display: 'flex',
                                        justifyContent: rowIndex % 2 === 0 ? 'flex-start' : 'flex-end',
                                        width: '100%',
                                    }}
                                >
                                    {Array.from({ length: 10 }).map((_, colIndex) => (
                                        <span key={colIndex} style={{ padding: '0 8px' }}>
                                            {watermarkCurrentEmail}
                                        </span>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* Back button */}
                    {!isFullscreen && (
                        <Button
                            variant="contained"
                            onClick={handleBack}
                            style={{
                                position: 'fixed',
                                top: 10,
                                left: 10,
                                zIndex: 20,
                                padding: '8px 12px',
                                fontSize: 16,
                                background: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                            }}
                            aria-label="Back"
                        >
                            <ArrowBackIosNewOutlinedIcon />
                        </Button>
                    )}
                    {/* Fullscreen toggle button */}
                    <Button
                        variant="contained"
                        onClick={toggleFullscreen}
                        style={{
                            position: 'fixed',
                            top: 10,
                            right: 10,
                            zIndex: 20,
                            padding: '8px 12px',
                            fontSize: 16,
                            background: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                        }}
                        aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                    >
                        {isFullscreen ? <FullscreenExitOutlinedIcon /> : <FullscreenOutlinedIcon />}
                    </Button>
                </>
            )}

        </div>
    );
};

export default WatchVimeoVideoPlayerPage;
